<script setup lang="ts">
import { computed, ref } from 'vue';
import type { SpacingProp } from '@/types/Layout';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import { useLayout } from '@/composables/useLayout';

type DisplayMode = 'list' | 'slide';

type MediaType = 'game' | 'casino' | 'sports';

type Button = {
  url: string;
  text: string;
};

type Props = {
  button?: Button;
  items?: AchRelatedGame[];
  title?: string;
  iconPath?: string;
  seeMoreUrl?: string;
  type?: MediaType;
  isLoading?: boolean;
  isLoadingMoreGames?: boolean;
  slug?: string;
  displayMode?: DisplayMode;
  /**
   * Items per row.
   * In order as number or number list
   *
   * itemsPerRow or [desktop, tablet, mobile]
   *
   * Ex: 8 or [8, 6, 4]
   */
  perRow?: SpacingProp;
  /**
   * Space between items.
   * In order as number or number list
   *
   * spaceBetweenItems or [desktop, tablet, mobile]
   *
   * Ex: 24 or [24, 16, 12]
   */
  spaceBetween?: SpacingProp;
  isFirstChildBigger?: boolean;
  shouldNormalizeThumbnail?: boolean;
  cardHeight?: string;
  showNumbers?: boolean;
  modalVisible?: boolean;
  zIndex?: number;
};

const emit = defineEmits(['update:modalVisible']);

const {
  button,
  items,
  title = 'Jogos Relacionados',
  iconPath,
  type = 'game',
  isLoading = false,
  isLoadingMoreGames,
  displayMode = 'slide',
  perRow = [6, 4, 3.4],
  spaceBetween = [24, 16, 10],
  isFirstChildBigger = false,
  cardHeight = 'auto',
  showNumbers = false,
  modalVisible = false,
} = defineProps<Props>();

// Modal state
const isRelatedGamesModalVisible = ref(modalVisible);

watch(
  () => modalVisible,
  (newVal) => {
    isRelatedGamesModalVisible.value = newVal;
  },
);

watch(isRelatedGamesModalVisible, (newVal) => {
  emit('update:modalVisible', newVal);
});

function handleSeeMoreClick() {
  isRelatedGamesModalVisible.value = true;
}

const { getSpacingMeasurementFromPropList, isMobile } = useLayout();

const relatedGamesSwiperRef = ref<InstanceType<
  typeof PerfilOficialSwiper
> | null>(null);

// Transform AchRelatedGame to Game format expected by CasinoMedia
const adaptedGames = computed(() => {
  if (!items || !Array.isArray(items)) return [];

  const mappedGames = items
    .filter((achGame) => achGame.game_public_meta)
    .map((achGame) => ({
      id: achGame.ext_game_id,
      slug: achGame.game_public_meta!.link.replace(/^\/games\//, ''),
      name: achGame.game_public_meta!.name,
      image: achGame.game_public_meta!.image,
      provider: {
        id: 0, // Default provider ID
        name: achGame.game_public_meta!.game_provider,
      },
      order: null, // Required by Game interface
      games: null, // Required by Game interface
      enabled: achGame.game_public_meta!.enabled,
      game_categories: achGame.game_public_meta!.game_categories,
    }));

  // Ordenar os jogos: primeiro os que têm números em game_categories, ordenados pelo menor número, depois os que não têm
  return [...mappedGames].sort((a, b) => {
    const getMinNumber = (game: any) => {
      const cats = game.game_categories;

      if (typeof cats === 'number' && !Number.isNaN(cats)) return cats;

      if (Array.isArray(cats)) {
        const numbers = cats
          .filter((cat) => {
            if (typeof cat === 'number' && !Number.isNaN(cat)) return true;

            if (typeof cat === 'string') {
              const num = Number.parseFloat(cat);

              return !Number.isNaN(num) && Number.isFinite(num);
            }

            if (typeof cat === 'object' && cat && typeof cat.id === 'number')
              return true;

            return false;
          })
          .map((cat) => {
            if (typeof cat === 'string') return Number.parseFloat(cat);

            if (typeof cat === 'object' && cat) return cat.id;

            return cat;
          });

        return numbers.length > 0 ? Math.min(...numbers) : Number.NaN;
      }

      return Number.NaN;
    };

    const aMin = getMinNumber(a);
    const bMin = getMinNumber(b);

    // Se ambos não têm números, manter ordem original
    if (Number.isNaN(aMin) && Number.isNaN(bMin)) return 0;
    // Se a não tem, b tem, a vai para depois
    if (Number.isNaN(aMin)) return 1;
    // Se b não tem, a tem, a vai para antes
    if (Number.isNaN(bMin)) return -1;

    // Ambos têm, ordenar pelo menor número
    return aMin - bMin;
  });
});

const firstMediaItemRef = ref<HTMLDivElement | null>(null);

const isSlideMode = computed<boolean>(() => {
  return displayMode === 'slide';
});

const isGameMediaType = computed<boolean>(() => {
  return type === 'game';
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const spaceBetweenInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(perRow, 6);
});

const mediaItemWidth = computed<string>(() => {
  const gapValue = spaceBetweenItems.value;
  const items = itemsPerRow.value;

  return `calc((100% - ${(items - 1) * gapValue}px) / ${items})`;
});

// Define the offset for each item in the list.
// Each item in the array can be:
// - A single number (e.g., 40) for a fixed offset across all breakpoints.
// - An array of three numbers [desktop, tablet, mobile] for breakpoint-specific offsets.
//   - desktop: offset for screens >= 1024px
//   - tablet: offset for screens >= 768px and < 1024px
//   - mobile: offset for screens < 768px
const numberOffsets: SpacingProp[] = [
  [55, 48, 37], // Offset for item 1 (index 0)
  [55, 55, 35], // Offset for item 2 (index 1)
  [55, 55, 35], // Offset for item 3 (index 2)
  [55, 55, 45], // Offset for item 4 (index 3)
  [55, 55, 35], // Offset for item 5 (index 4)
  [55, 55, 35], // Offset for item 6 (index 5)
  [55, 55, 35], // Offset for item 7 (index 6)
  [55, 55, 35], // Offset for item 8 (index 7)
  [55, 55, 35], // Offset for item 9 (index 8)
  [130, 120, 70], // Offset for item 10 (index 9)
];

// Navigation controls
const canScrollPrev = computed(() => {
  if (!relatedGamesSwiperRef.value) return false;

  return relatedGamesSwiperRef.value.currentScrollLeft > 0;
});

const canScrollNext = computed(() => {
  if (!relatedGamesSwiperRef.value) return false;

  const { currentScrollLeft, currentClientWidth, currentScrollWidth } =
    relatedGamesSwiperRef.value;

  return currentScrollLeft + currentClientWidth < currentScrollWidth;
});
</script>

<template>
  <div
    class="mb-4"
    :class="$style.relatedGames"
  >
    <template v-if="isLoading">
      <div :class="$style.title">
        <BaseCommonSkeleton
          v-if="title"
          :class="$style.skeletonTitle"
        />
        <BaseCommonSkeleton
          v-if="button"
          :class="$style.skeletonButton"
        />
      </div>

      <div
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
      >
        <BaseCommonSkeleton
          v-for="i in isSlideMode ? itemsPerRow : 24"
          :key="i"
          :class="$style.skeletonItem"
        />
      </div>
    </template>

    <template v-else>
      <!-- Header customizado para o Ver Mais sempre visível -->
      <div
        v-if="isSlideMode"
        :class="$style.customHeader"
      >
        <div :class="$style.titleContainer">
          <NuxtIcon
            v-if="iconPath"
            class="text-[36px]"
            :filled="true"
            :name="iconPath"
          />
          <p class="text-white text-lg font-bold leading-4">{{ title }}</p>
        </div>

        <div :class="$style.rightSection">
          <!-- Botão Ver Mais - sempre visível mas ícone só no mobile -->
          <button
            class="flex items-center justify-center gap-1 text-[10px] lg:text-[15px] text-white font-bold leading-4 hover:text-white/80 transition-colors cursor-pointer"
            @click="handleSeeMoreClick"
          >
            <span>VER MAIS</span>
            <NuxtIcon
              v-if="isMobile"
              class="text-[15px] text-white px-2"
              name="perfil-oficial/next"
            />
          </button>

          <!-- Navegadores - apenas no desktop -->
          <div
            v-if="!isMobile"
            :class="$style.navigationButtons"
          >
            <button
              class="flex items-center justify-center rounded-full text-white disabled:opacity-50 px-2.5"
              :disabled="!canScrollPrev"
              @click="relatedGamesSwiperRef?.scrollPrev()"
            >
              <NuxtIcon
                class="text-[15px]"
                name="perfil-oficial/back"
              />
            </button>
            <button
              class="flex items-center justify-center rounded-full bg-gray-700 text-white disabled:opacity-50 px-2.5"
              :disabled="!canScrollNext"
              @click="relatedGamesSwiperRef?.scrollNext()"
            >
              <NuxtIcon
                class="text-[15px]"
                name="perfil-oficial/next"
              />
            </button>
          </div>
        </div>
      </div>

      <PerfilOficialSwiper
        v-if="isSlideMode"
        ref="relatedGamesSwiperRef"
        :card-height="cardHeight"
        :gap="spaceBetweenInPixels"
        :icon-path="''"
        :see-more-url="''"
        :title="''"
      >
        <template #default="{ handleClick }">
          <div
            v-for="(game, index) in adaptedGames"
            :key="game.slug || index"
            :ref="
              (el) => {
                if (index === 0) firstMediaItemRef = el as HTMLDivElement;
              }
            "
            :class="[
              $style.mediaItemWrapper,
              $style.swiperItem,
              { [$style.disabledMediaItem]: !game.slug },
              { [$style.hasNumbersOffset]: showNumbers },
            ]"
            :style="{
              '--casino-media-content-width': `${firstMediaItemRef ? firstMediaItemRef.offsetWidth - 40 : 0}px`,
              '--number-offset-left': `${getSpacingMeasurementFromPropList(numberOffsets[index] || [55, 48, 37], 40)}px`,
            }"
          >
            <CasinoMediaApostatudo
              :class="[$style.mediaCard, { [$style.isGame]: isGameMediaType }]"
              :game="game"
              :index="index"
              :type="type as any"
              @click="handleClick"
            />
            <div
              v-if="showNumbers"
              :class="$style.mediaItemNumber"
            >
              <NuxtImg
                :alt="`Number ${index + 1}`"
                class="py-1"
                :src="
                  getAssetsUrlForCDN(
                    `/assets/images/perfil-oficial/numbers/${index + 1}.png`,
                  )
                "
              />
            </div>
            <div
              v-if="!game.slug || game.slug === ''"
              :class="$style.comingSoonOverlay"
            >
              <span :class="$style.comingSoonText">EM BREVE</span>
            </div>
          </div>
        </template>
      </PerfilOficialSwiper>

      <div
        v-else
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
      >
        <div
          v-for="(game, index) in adaptedGames"
          :key="game.slug || index"
          :ref="
            (el) => {
              if (index === 0) firstMediaItemRef = el as HTMLDivElement;
            }
          "
          :class="[
            $style.mediaItemWrapper,
            { [$style.disabledMediaItem]: !game.slug },
            { [$style.hasNumbersOffset]: showNumbers },
          ]"
          :style="{
            '--casino-media-content-width': `${firstMediaItemRef ? firstMediaItemRef.offsetWidth - 40 : 0}px`,
            '--number-offset-left': `${getSpacingMeasurementFromPropList(numberOffsets[index] || [55, 48, 37], 40)}px`,
          }"
        >
          <CasinoMediaApostatudo
            :class="[$style.mediaCard, { [$style.isGame]: isGameMediaType }]"
            :game="game"
            :type="type as any"
          />
          <div
            v-if="showNumbers"
            :class="$style.mediaItemNumber"
          >
            <NuxtImg
              :alt="`Number ${index + 1}`"
              :src="
                getAssetsUrlForCDN(
                  `/assets/images/perfil-oficial/numbers/${index + 1}.png`,
                )
              "
            />
          </div>
          <div
            v-if="!game.slug"
            :class="$style.comingSoonOverlay"
          >
            <span :class="$style.comingSoonText">EM BREVE</span>
          </div>
        </div>
        <template v-if="isLoadingMoreGames">
          <BaseCommonSkeleton
            v-for="index in 24"
            :key="`skeleton-${index}`"
            :class="$style.skeletonItem"
          />
        </template>
      </div>
    </template>

    <!-- Modal para Ver Mais Jogos -->
    <BaseModalListItems
      v-model="isRelatedGamesModalVisible"
      :desktop-size-factor="1.3"
      :items="adaptedGames"
      :title="title"
      :z-index="zIndex"
    />
  </div>
</template>

<style module lang="postcss">
.mediaItemNumber {
  @apply absolute select-none pointer-events-none;
  left: calc(var(--number-offset-left) * -1);
  top: 50%;
  transform: translateY(-50%);
  z-index: 0; /* Ensure it's behind the card */
  height: 100%; /* Make it take full height of parent */
  mask-image: linear-gradient(
    to right,
    white 100%,
    white 100%
  ); /* Initially fully opaque */
  mask-mode: alpha;
  transition: mask-image 0.3s ease-in-out;

  img {
    @apply h-auto; /* Adjust size as needed */
    height: 100%; /* Make image take full height */
    object-fit: contain; /* Maintain aspect ratio */
  }
}

.mediaCard {
  @apply relative;
  z-index: 1; /* Increased z-index to ensure it's above the number */
}

.titleContainer {
  @apply flex items-center justify-center gap-4;
}

.relatedGames {
  @apply flex flex-col select-none w-full;
}

.title {
  @apply flex justify-start items-center;
  line-height: 1.4;
}

.items {
  @apply grid w-full;

  gap: v-bind(spaceBetweenInPixels);
  grid-template-columns: repeat(v-bind(itemsPerRow), minmax(0, 1fr));

  &.isFirstChildBigger {
    @media (min-width: 1024px) {
      a.mediaItem.isGame {
        &:nth-child(1) {
          grid-area: 1 / 1 / 3 / 3;
        }
      }
    }
  }
}

.skeletonTitle {
  @apply h-[20px] max-w-[200px] flex-1;
}

.skeletonButton {
  @apply h-[20px] max-w-[60px] flex-1;
}

.skeletonItem {
  @apply w-full h-auto aspect-[25/29];
}

.navigationButton {
  @apply hover:text-primary cursor-pointer text-texts/60;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.swiperItem {
  min-width: v-bind(mediaItemWidth);
  flex-shrink: 0;
}

.mediaItemWrapper {
  @apply relative;
  z-index: 1; /* Ensure a stacking context for its children */

  &:hover .mediaItemNumber {
    mask-image: linear-gradient(
      to right,
      white 25%,
      transparent 100%
    ); /* Fade right half */
  }
}

.disabledMediaItem {
  pointer-events: none;
  cursor: default;
}

.comingSoonOverlay {
  @apply absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white text-lg font-bold rounded-md overflow-hidden;
  animation: pulse 1.5s infinite alternate;
  z-index: 90;
}

.comingSoonText {
  @apply text-[15px] md:text-[18px];
  background: linear-gradient(90deg, #ffffff, #ffffff70, #ffffff);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradient-animation 3s linear infinite;
}

.hasNumbersOffset {
  margin-left: var(
    --number-offset-left
  ); /* Use CSS variable for dynamic margin-left */
}

@keyframes gradient-animation {
  to {
    background-position: 200% center;
  }
}

@keyframes pulse {
  0% {
    opacity: 0.84;
  }
  100% {
    opacity: 0.9;
  }
}

.customHeader {
  @apply flex justify-between items-center mb-4 rounded-md mt-4;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

.rightSection {
  @apply flex items-center gap-3;
}

.navigationButtons {
  @apply flex items-center justify-center;
}
</style>
